import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { ApiError, authApi } from '../api';
import { usePreAuthStore } from '../pre-auth-store';
import { finalizeTokens } from '../session-helpers';
import { relativeTime } from '@/lib/datetime';

export function TenantPickerPage() {
  const navigate = useNavigate();
  const preAuth = usePreAuthStore((s) => s.data);
  const clearPreAuth = usePreAuthStore((s) => s.clear);
  const [error, setError] = useState<string | null>(null);
  const [pendingId, setPendingId] = useState<string | null>(null);

  if (!preAuth) {
    return (
      <div className="grid min-h-screen place-items-center">
        <div className="card text-center">
          <p className="text-sm text-muted-foreground">
            No active pre-auth session. Please sign in again.
          </p>
          <button onClick={() => navigate('/login')} className="btn-primary mt-4">
            Go to login
          </button>
        </div>
      </div>
    );
  }

  const pickTenant = async (tenantId: string) => {
    setError(null);
    setPendingId(tenantId);
    try {
      const tokens = await authApi.selectTenant(preAuth.preAuthToken, tenantId);
      await finalizeTokens(tokens);
      clearPreAuth();
      navigate('/command-center', { replace: true });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Unable to switch workspace');
    } finally {
      setPendingId(null);
    }
  };

  return (
    <div className="grid min-h-screen place-items-center px-4">
      <div className="card w-full max-w-md">
        <h1 className="text-xl font-semibold">Choose your workspace</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          You belong to multiple Fleet Iris workspaces.
        </p>

        <ul className="mt-6 space-y-3">
          {preAuth.memberships.map((m) => (
            <li key={m.tenant_id}>
              <button
                onClick={() => pickTenant(m.tenant_id)}
                disabled={pendingId !== null}
                className="flex w-full items-center justify-between rounded border border-border bg-background px-4 py-3 text-left transition-colors hover:border-primary hover:bg-muted disabled:opacity-50"
              >
                <div>
                  <div className="font-medium">{m.tenant_name}</div>
                  <div className="text-xs text-muted-foreground">
                    {m.role}
                    {m.last_accessed_at && <> • Last accessed {relativeTime(m.last_accessed_at)}</>}
                  </div>
                </div>
                <span aria-hidden>→</span>
              </button>
            </li>
          ))}
        </ul>

        {error && (
          <p className="mt-4 rounded bg-danger/10 px-3 py-2 text-xs text-danger" role="alert">
            {error}
          </p>
        )}
      </div>
    </div>
  );
}
